import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./dropdownfilter.module.css";
import DropDownIcon from "../Icons/DropDownIcon";
import SearchBar from "../Search/SearchBar";
import { optionLabelMatchesSearch } from "../../Utils/utils";

const DropdownNoLabel = ({
	options,
	defaultOption,
	onSelect,
	selectedLabel,
	selectedValue,
	dropdownClass,
	dropdownMenuClass,
	placeholder = "Select an option",
	placeholderClass = styles.placeholder,
	showGenderCircle = false,
	onDropdownToggle = null,
	disabled = false,
}) => {
	const [selectedOption, setSelectedOption] = useState(defaultOption);
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [keyboardSelectedIndex, setKeyboardSelectedIndex] = useState(-1);
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);

	useEffect(() => {
		setSelectedOption(selectedLabel || defaultOption);
	}, [selectedLabel, defaultOption]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Auto-scroll dropdown into view inside scrollable parent
	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				// behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	// Auto-scroll selected option into view within dropdown
	useEffect(() => {
		if (!isDropdownOpen || !dropdownMenuRef.current || !selectedValue) return;

		const scrollToSelected = () => {
			const selectedElement = dropdownMenuRef.current?.querySelector(`[data-selected="true"]`);
			if (selectedElement) {
				selectedElement.scrollIntoView({
					block: "center",
				});
			}
		};

		scrollToSelected();
		const rafId = requestAnimationFrame(scrollToSelected);
		return () => cancelAnimationFrame(rafId);
	}, [isDropdownOpen, selectedValue, options]);

	const handleSelect = useCallback((option) => {
		const selected = typeof option === "string" ? option : option.label;
		setSelectedOption(selected);
		onSelect(option);
		setDropdownOpen(false);
		setSearchQuery(""); // Clear search when option is selected
		setKeyboardSelectedIndex(-1);
	}, [onSelect]);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (!isDropdownOpen) return;

			const filteredOptions = options.filter((option) => {
				const label = typeof option === "string" ? option : option.label;
				return optionLabelMatchesSearch(label, searchQuery);
			});

			switch (event.key) {
				case 'ArrowDown':
					event.preventDefault();
					setKeyboardSelectedIndex(prev => {
						const nextIndex = Math.min(prev + 1, filteredOptions.length - 1);
						// Scroll the selected item into view
						setTimeout(() => {
							const selectedElement = dropdownMenuRef.current?.querySelector(`[data-keyboard-selected="true"]`);
							if (selectedElement) {
								selectedElement.scrollIntoView({
									behavior: "smooth",
									block: "center",
								});
							}
						}, 0);
						return nextIndex;
					});
					break;
				case 'ArrowUp':
					event.preventDefault();
					setKeyboardSelectedIndex(prev => {
						const nextIndex = Math.max(prev - 1, -1);
						// Scroll the selected item into view
						setTimeout(() => {
							const selectedElement = dropdownMenuRef.current?.querySelector(`[data-keyboard-selected="true"]`);
							if (selectedElement) {
								selectedElement.scrollIntoView({
									behavior: "smooth",
									block: "center",
								});
							}
						}, 0);
						return nextIndex;
					});
					break;
				case 'Enter':
					event.preventDefault();
					if (keyboardSelectedIndex >= 0 && keyboardSelectedIndex < filteredOptions.length) {
						handleSelect(filteredOptions[keyboardSelectedIndex]);
					}
					break;
				case 'Escape':
					event.preventDefault();
					setDropdownOpen(false);
					setKeyboardSelectedIndex(-1);
					break;
				default:
					// Do nothing for other keys
					break;
			}
		};

		if (isDropdownOpen) {
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [isDropdownOpen, keyboardSelectedIndex, options, searchQuery, handleSelect]);

	// Reset keyboard selection when dropdown opens
	useEffect(() => {
		if (isDropdownOpen) {
			// Find the index of the currently selected option
			const filteredOptions = options.filter((option) => {
				const label = typeof option === "string" ? option : option.label;
				return optionLabelMatchesSearch(label, searchQuery);
			});
			
			const selectedIndex = filteredOptions.findIndex((option) => {
				const label = typeof option === "string" ? option : option.label;
				const value = typeof option === "string" ? option : option.value;
				return selectedValue && (value === selectedValue || label === selectedValue);
			});
			
			setKeyboardSelectedIndex(selectedIndex);
		}
	}, [isDropdownOpen, selectedValue, options, searchQuery]);

	// console.log("Dropdown Props:", { selectedLabel, selectedOption, options });
	const displayOption = selectedOption || placeholder;
	// console.log("Displaying Option:", displayOption);

	// Filter options based on search query
	const filteredOptions = options.filter((option) => {
		const label = typeof option === "string" ? option : option.label;
		return optionLabelMatchesSearch(label, searchQuery);
	});

	return (
		<div
				ref={dropdownRef}
				className={`${dropdownClass} commonDropdownNoImage ${disabled ? "disabled" : ""}`}
				tabIndex={0}>
				<div
						className={`dropdownToggle ${
							!selectedLabel ? placeholderClass : ""
						} cursor-pointer`}
						onClick={() => {
							const newState = !isDropdownOpen;
							setDropdownOpen(newState);
							if (onDropdownToggle) {
								onDropdownToggle(newState);
							}
						}}>
						{selectedLabel || displayOption}
						{!selectedLabel && (
							<DropDownIcon 
								size={12} 
								color="#6b7280" 
								className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.rotated : ''}`}
							/>
						)}
					</div>
			{isDropdownOpen && (
				<ul className={`${dropdownMenuClass} dropdownMenu`} ref={dropdownMenuRef}>
					{/* Search Bar */}
					<li className="dropdownItem" style={{ padding: "0", borderBottom: "1px solid #eee" }}>
						<SearchBar
							placeholder="Search..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="border-0"
						/>
					</li>
					{filteredOptions.length > 0 ? (
						filteredOptions.map((option, index) => {
							const label = typeof option === "string" ? option : option.label;
							const value = typeof option === "string" ? option : option.value;
							const isSelected = selectedValue && (value === selectedValue || label === selectedValue);
							const isKeyboardSelected = keyboardSelectedIndex === index;

							return (
								<li
									key={index}
									className={`dropdownItem ${isSelected ? styles.selectedItem : ''} ${isKeyboardSelected ? styles.keyboardSelected : ''}`}
									data-selected={isSelected}
									data-keyboard-selected={isKeyboardSelected}
									onClick={() => handleSelect(option)}>
									<span
										style={{
											display: "flex",
											alignItems: "center",
										}}>
										{showGenderCircle && option.gender && (
											<span className={`genderCircle ${
													option.gender.toLowerCase() === "female" ? "female" : "male"
												}`}>
												{/* {option.gender.toLowerCase() === "female" ? "F" : "M"} */}
												{option.pager}
											</span>
										)}
										{label}
									</span>
								</li>
							);
						})
					) : (
						<li className="dropdownItem" style={{ color: "#999", fontStyle: "italic" }}>
							No options found
						</li>
					)}
				</ul>
			)}
		</div>
	);
};

export default DropdownNoLabel;
